import { db, Media } from '../db';

/**
 * Saves a media blob to IndexedDB
 * @param sessionId The ID of the scouting session this media belongs to
 * @param blob The image/media blob to save
 * @returns The ID of the saved media record
 */
export const saveMedia = async (sessionId: number, blob: Blob): Promise<number> => {
  // Materialise into a plain Blob before storing — Safari iOS does not reliably
  // round-trip File objects (from camera inputs) through IndexedDB, especially
  // after a subsequent modify() on the record.
  const buffer = await blob.arrayBuffer();
  const media: Media = {
    sessionId,
    data: new Blob([buffer], { type: blob.type }),
    type: blob.type
  };
  return await db.media.add(media) as number;
};

/**
 * Retrieves media from IndexedDB by its ID
 * @param id The ID of the media record to retrieve
 * @returns The media record or undefined if not found
 */
export const getMedia = async (id: number): Promise<Media | undefined> => {
  return await db.media.get(id);
};

/**
 * Deletes media from IndexedDB by its ID
 * @param id The ID of the media record to delete
 */
export const deleteMedia = async (id: number): Promise<void> => {
  await db.media.delete(id);
};

/**
 * Retrieves all media associated with a specific session
 * @param sessionId The ID of the scouting session
 * @returns An array of media records
 */
export const getMediaBySessionId = async (sessionId: number): Promise<Media[]> => {
  return await db.media.where('sessionId').equals(sessionId).toArray();
};
